// src/components/Dashboard/CoachDashboard/CoachDashboard.jsx
// Main Coach Dashboard - Tactical, action-focused dashboard for coaches

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaSyncAlt, FaChalkboardTeacher } from 'react-icons/fa';
import { useUser } from '../../../context/UserContext';
import { getUserEnrolledPrograms } from '../../../api/enrolledPrograms';
import { getCoachingPrograms, isCoachRole } from '../../../utils/personaDetection';
import ProgramSelectorBar from './ProgramSelectorBar';
import TodaysAgenda from './TodaysAgenda';
import MyTeamsWidget from './MyTeamsWidget';
import RSVPHealthWidget from './RSVPHealthWidget';
import QuickActionsBar from './QuickActionsBar';
import CoachAwardsWidget from './CoachAwardsWidget';
import EquipmentStatusWidget from './EquipmentStatusWidget';
import PlayerSpotlightWidget from './PlayerSpotlightWidget';
import WeekCalendarWidget from './WeekCalendarWidget';
import styles from './CoachDashboard.module.css';

const STORAGE_KEY = 'coachDashboard_selectedProgramId';

const CoachDashboard = ({
  userClubs,
  activeMode,
  onModeChange,
  onExploreClick
}) => {
  const { user, userProfile } = useUser();
  
  // State
  const [coachingPrograms, setCoachingPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch programs where user is a coach
  const fetchCoachingPrograms = useCallback(async () => {
    if (!userClubs || !userProfile) {
      setCoachingPrograms([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const allPrograms = await getUserEnrolledPrograms(userClubs, userProfile);
      const coachPrograms = getCoachingPrograms(allPrograms);
      
      setCoachingPrograms(coachPrograms);
      
      // Restore selected program from localStorage or default to first
      const storedProgramId = localStorage.getItem(STORAGE_KEY);
      if (storedProgramId && coachPrograms.some(p => p._id === storedProgramId)) {
        setSelectedProgramId(storedProgramId);
      } else if (coachPrograms.length > 0) {
        setSelectedProgramId(coachPrograms[0]._id);
      }
    } catch (error) {
      console.error('Error fetching coaching programs:', error);
      setCoachingPrograms([]);
    } finally {
      setLoading(false);
    }
  }, [userClubs, userProfile]);

  // Initial fetch
  useEffect(() => {
    fetchCoachingPrograms();
  }, [fetchCoachingPrograms]);

  // Handle program selection
  const handleProgramSelect = (programId) => {
    setSelectedProgramId(programId);
    localStorage.setItem(STORAGE_KEY, programId);
  };

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchCoachingPrograms();
      setRefreshTrigger(prev => prev + 1);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Get selected program
  const selectedProgram = useMemo(() => {
    return coachingPrograms.find(p => p._id === selectedProgramId) || null;
  }, [coachingPrograms, selectedProgramId]);

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
          <p>Loading your coaching dashboard...</p>
        </div>
      </div>
    );
  }

  // No coaching programs
  if (coachingPrograms.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🏟️</div>
          <h2>No Coaching Assignments</h2>
          <p>You're not currently assigned as a coach to any programs.</p>
          <p className={styles.emptyHint}>Contact a program director to be added as a coach.</p>
          <button className={styles.exploreButton} onClick={onExploreClick}>
            Explore Programs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header Bar */}
      <div className={styles.headerBar}>
        <div className={styles.headerRight}>
          <button
            className={`${styles.refreshButton} ${isRefreshing ? styles.refreshing : ''}`}
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Refresh dashboard"
          >
            <FaSyncAlt />
          </button>
        </div>
      </div>

      {/* Program Selector */}
      <ProgramSelectorBar
        programs={coachingPrograms}
        selectedProgramId={selectedProgramId}
        onSelectProgram={handleProgramSelect}
      />

      {/* Today's Agenda - Hero Section */}
      <TodaysAgenda
        program={selectedProgram}
        refreshTrigger={refreshTrigger}
      />

      {/* Main Content Grid */}
      <div className={styles.mainGrid}>
        {/* Left Column */}
        <div className={styles.leftColumn}>
          {/* My Teams */}
          <MyTeamsWidget
            program={selectedProgram}
            userProfile={userProfile}
            refreshTrigger={refreshTrigger}
          />
          
          {/* Recent Awards */}
          <CoachAwardsWidget
            program={selectedProgram}
            userProfile={userProfile}
            refreshTrigger={refreshTrigger}
          />
        </div>

        {/* Right Column */}
        <div className={styles.rightColumn}>
          {/* RSVP Health */}
          <RSVPHealthWidget
            program={selectedProgram}
            refreshTrigger={refreshTrigger}
          />
          
          {/* Equipment Status */}
          <EquipmentStatusWidget
            program={selectedProgram}
            refreshTrigger={refreshTrigger}
          />
        </div>
      </div>

      {/* Quick Actions Bar */}
      <QuickActionsBar
        program={selectedProgram}
        userProfile={userProfile}
      />

      {/* Player Spotlight */}
      <PlayerSpotlightWidget
        program={selectedProgram}
        userProfile={userProfile}
        refreshTrigger={refreshTrigger}
      />

      {/* Week Calendar */}
      <WeekCalendarWidget
        programs={coachingPrograms}
        selectedProgramId={selectedProgramId}
        refreshTrigger={refreshTrigger}
      />
    </div>
  );
};

export default CoachDashboard;

